import React, { useMemo } from 'react';
import { Activity, RefreshCw, AlertTriangle, CheckCircle2, XCircle, Inbox as InboxIcon } from 'lucide-react';
import AppHeader from '../components/layout/AppHeader';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import ErrorState from '../components/shared/ErrorState';
import EmptyState from '../components/shared/EmptyState';
import { useSyncDashboard, useSyncRuns } from '../hooks/useSyncRuns';

function fmtDuration(ms) {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60_000)}m ${Math.floor((ms % 60_000) / 1000)}s`;
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function timeAgo(iso) {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffSec < 60) return `há ${diffSec}s`;
  const min = Math.floor(diffSec / 60);
  if (min < 60) return `há ${min}min`;
  const h = Math.floor(min / 60);
  if (h < 48) return `há ${h}h`;
  return `há ${Math.floor(h / 24)}d`;
}

function StatusBadge({ status }) {
  const map = {
    completed: { bg: 'bg-emerald-100 text-emerald-800', Icon: CheckCircle2 },
    running: { bg: 'bg-blue-100 text-blue-800', Icon: Activity },
    error: { bg: 'bg-red-100 text-red-800', Icon: XCircle },
    timeout: { bg: 'bg-amber-100 text-amber-800', Icon: AlertTriangle },
    partial: { bg: 'bg-amber-100 text-amber-800', Icon: AlertTriangle },
  };
  const c = map[status] || { bg: 'bg-slate-100 text-slate-600', Icon: InboxIcon };
  const Icon = c.Icon;
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium inline-flex items-center gap-0.5 ${c.bg}`}>
      <Icon size={10} /> {status || '—'}
    </span>
  );
}

function KpiCard({ label, value, hint, tone = 'slate' }) {
  const toneClass = {
    slate: 'bg-white border-slate-200',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    red: 'bg-red-50 border-red-200 text-red-900',
    blue: 'bg-blue-50 border-blue-200 text-blue-900',
  }[tone];
  return (
    <div className={`rounded-lg border p-3 ${toneClass}`}>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
      <div className="text-lg font-bold mt-0.5 tabular-nums">{value}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  );
}

export default function SyncStatusPage() {
  const dashboard = useSyncDashboard();
  const runsQuery = useSyncRuns({ limit: 50 });

  const kpis = useMemo(() => {
    const rows = dashboard.data || [];
    const totalInserted = rows.reduce((s, r) => s + (Number(r.total_inserted) || 0), 0);
    const inserted24h = rows.reduce((s, r) => s + (Number(r.inserted_24h) || 0), 0);
    const lastRunAt = rows.reduce((acc, r) => {
      if (!r.last_run_at) return acc;
      return !acc || new Date(r.last_run_at) > new Date(acc) ? r.last_run_at : acc;
    }, null);
    const totalErrors24h = (runsQuery.data || [])
      .filter((r) => {
        if (!r.started_at) return false;
        return Date.now() - new Date(r.started_at).getTime() < 24 * 3600 * 1000;
      })
      .reduce((s, r) => s + (Number(r.emails_errors) || 0) + (r.status === 'error' ? 1 : 0), 0);
    const totalMatch = rows.reduce(
      (acc, r) => ({
        contato: acc.contato + (Number(r.total_match_contato) || 0),
        rpc: acc.rpc + (Number(r.total_match_rpc) || 0),
        cascade: acc.cascade + (Number(r.total_match_cascade) || 0),
        none: acc.none + (Number(r.total_match_none) || 0),
      }),
      { contato: 0, rpc: 0, cascade: 0, none: 0 }
    );
    const totalMatchAttempts = totalMatch.contato + totalMatch.rpc + totalMatch.cascade + totalMatch.none;
    const matchRate = totalMatchAttempts > 0
      ? (((totalMatchAttempts - totalMatch.none) / totalMatchAttempts) * 100).toFixed(1) + '%'
      : '—';
    return { totalInserted, inserted24h, lastRunAt, totalErrors24h, matchRate, totalMatchAttempts, totalMatch };
  }, [dashboard.data, runsQuery.data]);

  const loading = dashboard.loading || runsQuery.loading;
  const error = dashboard.error || runsQuery.error;
  const refetchAll = () => {
    dashboard.refetch();
    runsQuery.refetch();
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader
        title="Sync Status"
        subtitle="Observabilidade do Gmail Sync — pipeline de emails"
        icon={Activity}
      />
      <main className="p-4 lg:p-6 max-w-7xl mx-auto space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-xs text-slate-500">
            Monitoramento das execuções dos Apps Scripts dos 3 colaboradores.
          </div>
          <button
            onClick={refetchAll}
            className="text-xs px-2 py-1 bg-white border border-slate-200 text-slate-700 rounded hover:bg-slate-50 flex items-center gap-1"
          >
            <RefreshCw size={12} /> Atualizar
          </button>
        </div>

        {loading && <LoadingSpinner label="Carregando runs..." />}
        {!loading && error && <ErrorState message={error} onRetry={refetchAll} />}

        {!loading && !error && (
          <>
            {/* KPIs */}
            <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KpiCard
                label="Total inseridos"
                value={kpis.totalInserted.toLocaleString('pt-BR')}
                hint={`${kpis.inserted24h.toLocaleString('pt-BR')} nas últimas 24h`}
                tone="blue"
              />
              <KpiCard
                label="Última execução"
                value={timeAgo(kpis.lastRunAt)}
                hint={fmtDateTime(kpis.lastRunAt)}
                tone="slate"
              />
              <KpiCard
                label="Erros 24h"
                value={kpis.totalErrors24h}
                hint={kpis.totalErrors24h > 0 ? 'atenção — verificar coluna Erros' : 'nenhum'}
                tone={kpis.totalErrors24h > 0 ? 'red' : 'emerald'}
              />
              <KpiCard
                label="Match rate"
                value={kpis.matchRate}
                hint={`${kpis.totalMatchAttempts.toLocaleString('pt-BR')} tentativas · ${kpis.totalMatch.none} sem match`}
                tone={
                  kpis.matchRate === '—' ? 'slate' :
                  parseFloat(kpis.matchRate) > 90 ? 'emerald' :
                  parseFloat(kpis.matchRate) > 70 ? 'amber' : 'red'
                }
              />
            </section>

            {/* Dashboard por colaborador */}
            {dashboard.data && dashboard.data.length > 0 && (
              <section className="bg-white rounded-lg border border-slate-200 p-4">
                <h2 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Por colaborador</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {dashboard.data.map((c) => (
                    <div key={c.colaborador_id} className="border border-slate-200 rounded p-3">
                      <div className="flex items-center justify-between mb-1">
                        <div className="font-medium text-sm capitalize">{c.colaborador_id}</div>
                        <div className="text-[10px] text-slate-400">v{c.last_version || '—'}</div>
                      </div>
                      <div className="text-xs text-slate-600">
                        Último: <strong>{timeAgo(c.last_run_at)}</strong>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-1 grid grid-cols-2 gap-x-2">
                        <span>Runs: <strong className="tabular-nums">{c.total_runs ?? 0}</strong></span>
                        <span>24h: <strong className="tabular-nums">{c.runs_24h ?? 0}</strong></span>
                        <span>Inseridos: <strong className="tabular-nums">{c.total_inserted ?? 0}</strong></span>
                        <span className={c.total_errors > 0 ? 'text-red-600' : ''}>
                          Erros: <strong className="tabular-nums">{c.total_errors ?? 0}</strong>
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Tabela de runs */}
            <section className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <div className="px-4 py-2 border-b border-slate-200 flex items-center justify-between">
                <h2 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Histórico de runs</h2>
                <span className="text-[11px] text-slate-500">{(runsQuery.data || []).length} runs</span>
              </div>
              {(!runsQuery.data || runsQuery.data.length === 0) ? (
                <EmptyState message="Nenhum run registrado" hint="Quando os Apps Scripts executarem, as runs aparecem aqui." icon={Activity} />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                      <tr>
                        <th className="px-2 py-2 text-left">Data/Hora</th>
                        <th className="px-2 py-2 text-left">Colaborador</th>
                        <th className="px-2 py-2 text-left">Status</th>
                        <th className="px-2 py-2 text-right">Inseridos</th>
                        <th className="px-2 py-2 text-right">Dup</th>
                        <th className="px-2 py-2 text-right">Erros</th>
                        <th className="px-2 py-2 text-right">Contato</th>
                        <th className="px-2 py-2 text-right">RPC</th>
                        <th className="px-2 py-2 text-right">Cascade</th>
                        <th className="px-2 py-2 text-right">Sem match</th>
                        <th className="px-2 py-2 text-right">Duração</th>
                        <th className="px-2 py-2 text-left">Versão</th>
                      </tr>
                    </thead>
                    <tbody>
                      {runsQuery.data.map((r) => (
                        <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                          <td className="px-2 py-1.5 text-slate-600 whitespace-nowrap">{fmtDateTime(r.started_at)}</td>
                          <td className="px-2 py-1.5 capitalize">{r.colaborador_id}</td>
                          <td className="px-2 py-1.5"><StatusBadge status={r.status} /></td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{r.emails_inserted ?? 0}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums text-slate-500">{r.duplicates_skipped ?? 0}</td>
                          <td className={`px-2 py-1.5 text-right tabular-nums ${(r.emails_errors || 0) > 0 ? 'text-red-600 font-semibold' : 'text-slate-400'}`}>
                            {r.emails_errors ?? 0}
                          </td>
                          <td className="px-2 py-1.5 text-right tabular-nums text-emerald-700">{r.match_contato ?? 0}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums text-blue-700">{r.match_rpc_domain ?? 0}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums text-violet-700">{r.match_cascade ?? 0}</td>
                          <td className={`px-2 py-1.5 text-right tabular-nums ${(r.match_none || 0) > 0 ? 'text-amber-700 font-semibold' : 'text-slate-400'}`}>
                            {r.match_none ?? 0}
                          </td>
                          <td className="px-2 py-1.5 text-right tabular-nums text-slate-500">{fmtDuration(r.duration_ms)}</td>
                          <td className="px-2 py-1.5 text-[10px] text-slate-500">{r.fn_version || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
